import { api } from "@/lib/api";
import {
  GuideDto,
  guideDtoListSchema,
  GuideDtoSchema,
  mapGuideFromDto,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getGuidesQueryOptions = queryOptions({
  queryKey: ["guides"],
  queryFn: async () => {
    const result: Array<GuideDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.get("/api/guides", guideDtoListSchema, {
        query: {
          page: page.toString(),
        },
        projection: ["Person"],
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(mapGuideFromDto);
  },
});

export function useGetGuides<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getGuidesQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getGuidesError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getGuideQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["guides", id],
    queryFn: () =>
      api
        .get("/api/guides/$id", GuideDtoSchema, {
          pathReplace: { $id: id },
          projection: ["Person"],
        })
        .then(mapGuideFromDto),
  });
}

export function useGetGuide<TERROR extends Error>(
  id: string,
  opts?: { onError?: (error: TERROR) => void; enabled?: boolean },
) {
  const { toast } = useToast();
  const q = useQuery({
    ...getGuideQueryOptions(id),
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getGuideDetailError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
